import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material'
import { DeleteForever, Restore } from '@mui/icons-material'

import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import {
    useCloseOrganisationMutation,
    useGetOrgDeletionQuery,
    useRestoreOrganisationMutation,
} from '@/apis/settingsApi'
import { logout } from '@/modules/auth/authSlice'
import { fmtDateTime } from '@/modules/reports/common'

import { confirmationMatches, daysUntil, roleFromToken } from './closeOrgModel'

/**
 * Owner-only "Close organisation" (Terms §17, docs/data-lifecycle.md): everything stops at
 * once, it can be restored for 30 days, then every store is purged and a deletion certificate
 * is recorded.
 */
export function CloseOrganisationCard({ tenantId, slug }: { tenantId: string; slug: string }) {
    const token = useAppSelector(s => s.auth.token)
    const dispatch = useAppDispatch()
    const isOwner = roleFromToken(token) === 'owner'
    const { data: deletion } = useGetOrgDeletionQuery(tenantId, { skip: !tenantId || !isOwner })
    const [closeOrg, closeState] = useCloseOrganisationMutation()
    const [restoreOrg, restoreState] = useRestoreOrganisationMutation()
    const [open, setOpen] = useState(false)
    const [typed, setTyped] = useState('')
    /** Set once closed: the session ends (the org is no longer active), restore runs until then. */
    const [closedUntil, setClosedUntil] = useState<string | null>(null)

    if (!isOwner) {
        return null
    }
    const notify = (message: string, severity: 'success' | 'error') =>
        dispatch(showNotification({ message, severity }))
    const scheduled = deletion?.status === 'scheduled'
    const purging = deletion?.status === 'purging' || deletion?.status === 'completed'

    return (
        <Card sx={{ mb: 3, borderColor: 'error.main' }} variant="outlined">
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Close organisation
                </Typography>
                {scheduled && deletion ? (
                    <>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This organisation was closed on {fmtDateTime(deletion.requestedAt)}.
                            Collection and API access are stopped. You can restore it for{' '}
                            {daysUntil(deletion.purgeAfter)} more day(s); on{' '}
                            {fmtDateTime(deletion.purgeAfter)} its data is deleted for good.
                        </Alert>
                        <Button
                            variant="contained"
                            startIcon={<Restore />}
                            disabled={restoreState.isLoading}
                            onClick={() => {
                                restoreOrg(tenantId)
                                    .unwrap()
                                    .then(() => notify('Organisation restored.', 'success'))
                                    .catch(() => notify('Restore failed.', 'error'))
                            }}
                        >
                            Restore organisation
                        </Button>
                    </>
                ) : purging ? (
                    <Alert severity="info">
                        This organisation&apos;s data is being or has been deleted. A deletion
                        certificate is available from support.
                    </Alert>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Stops collection and API access for every project at once. For 30 days
                            you can restore everything. After that all analytics data, the raw
                            archive, projects, keys and settings are deleted and a deletion
                            certificate is recorded. Your account record is kept for up to 24 months
                            and billing records for 8 years, as the privacy policy explains.
                        </Typography>
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteForever />}
                            onClick={() => setOpen(true)}
                        >
                            Close organisation
                        </Button>
                    </>
                )}
            </CardContent>
            <Dialog
                open={open}
                onClose={() => !closedUntil && setOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Close this organisation?</DialogTitle>
                <DialogContent>
                    {closedUntil ? (
                        <Alert severity="success">
                            The organisation is closed and collection has stopped. It can be
                            restored until {fmtDateTime(closedUntil)} (contact support, or an owner
                            can call POST /v1/tenants/:id/restore). After that its data is deleted
                            for good. You will now be signed out.
                        </Alert>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Everything stops now. You have 30 days to restore it; then the data
                                is deleted for good. Type <b>{slug}</b> to confirm.
                            </Typography>
                            <Box>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    size="small"
                                    label="Organisation slug"
                                    value={typed}
                                    onChange={e => setTyped(e.target.value)}
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {closedUntil ? (
                        <Button variant="contained" onClick={() => dispatch(logout())}>
                            Sign out
                        </Button>
                    ) : (
                        <>
                            <Button onClick={() => setOpen(false)}>Cancel</Button>
                            <Button
                                color="error"
                                variant="contained"
                                disabled={!confirmationMatches(typed, slug) || closeState.isLoading}
                                onClick={() => {
                                    closeOrg(tenantId)
                                        .unwrap()
                                        .then(r => {
                                            setTyped('')
                                            setClosedUntil(r.deletion.purgeAfter)
                                        })
                                        .catch(() => notify('Closing failed.', 'error'))
                                }}
                            >
                                Close organisation
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Card>
    )
}
